import { ServiceSummary } from '@pages/AdditionalServices/containers/methods/basket/getSummaryForServices';
import { RouteInGroup } from '@services/requests/orderService/types';
import { CurrencyData } from '@services/requests/searchLoaders/currencyLoader/CurrencyLoaderQuery';

/**
 * getRouteSummary вычисляет итоги по маршруту. Используется для вывода итоговой
 * информации по маршруту.
 * @param routes
 * @param currencies
 * @param targetCurrency
 * @param includeTax
 */
const getRouteSummary = (
  routes: RouteInGroup[],
  currencies: CurrencyData[],
  targetCurrency: string,
  includeTax: boolean = false
): ServiceSummary => {
  const result: ServiceSummary = {
    Currencies: {},
    Price: 0,
    Conversion: 0,
    Tax: 0,
    Total: 0,
    TargetCurrency: targetCurrency,
  };

  routes.map((route) => {
    route.steps.map((step) => {
      const conditions = [
        { cond: step.endTerminalPrice, val: step.endTerminalCalculatedPrice },
        { cond: step.startTerminalPrice, val: step.startTerminalCalculatedPrice },
        { cond: step.shoulderOfferPriceCondition, val: step.shoulderOfferCalculatedPrice },
        ...Array.from([
          ...(step.endTerminalAllowances ?? []),
          ...(step.startTerminalAllowances ?? []),
          ...(step.shoulderAllowances ?? []),
        ]).map((allowance) => ({
          cond: allowance.allowanceOfferCondition,
          val: allowance.calculatedPrice,
        })),
      ];

      conditions.map(({ cond, val }) => {
        if (!cond || !val) {
          return;
        }

        const sourceCurrency = currencies.find((c) => c.id === cond.currency_id);

        if (!!sourceCurrency) {
          if (!result.Currencies[sourceCurrency.code]) {
            result.Currencies[sourceCurrency.code] = 0;
          }

          result.Currencies[sourceCurrency.code] += val.basePrice;
          if (includeTax) {
            if (!result.Currencies[targetCurrency]) {
              result.Currencies[targetCurrency] = 0;
            }

            result.Currencies[targetCurrency] += val.taxInTargetCurrency;
          }

          result.Conversion += val.conversionFee;
          result.Tax += val.taxInTargetCurrency;
          result.Total += val.fullPriceInTargetCurrency;
          result.Price += val.priceInTargetCurrency;
        }
      });
    });

    const crossDockingOperations = route.mixedTransport?.crossDockingOperations ?? [];

    if (crossDockingOperations.length > 0) {
      crossDockingOperations.map((operation) => {
        const allowanceConditions = [
          ...Array.from([...(operation.allowances ?? [])]).map((allowance) => ({
            cond: allowance.allowanceOfferCondition,
            val: allowance.calculatedPrice,
          })),
        ];

        allowanceConditions.map(({ cond, val }) => {
          if (!cond || !val) {
            return;
          }

          const sourceCurrency = currencies.find((c) => c.id === cond.currency_id);

          if (!!sourceCurrency) {
            if (!result.Currencies[sourceCurrency.code]) {
              result.Currencies[sourceCurrency.code] = 0;
            }

            result.Currencies[sourceCurrency.code] += val.basePrice;
            if (includeTax) {
              if (!result.Currencies[targetCurrency]) {
                result.Currencies[targetCurrency] = 0;
              }

              result.Currencies[targetCurrency] += val.taxInTargetCurrency;
            }

            result.Conversion += val.conversionFee;
            result.Tax += val.taxInTargetCurrency;
            result.Total += val.fullPriceInTargetCurrency;
            result.Price += val.priceInTargetCurrency;
          }
        });
      });

      crossDockingOperations.map((operation) => {
        operation.priceParts.map((part) => {
          const sourceCurrency = currencies.find((c) => c.id === part.offerCondition.currency_id);

          if (!!sourceCurrency) {
            if (!result.Currencies[sourceCurrency.code]) {
              result.Currencies[sourceCurrency.code] = 0;
            }

            result.Currencies[sourceCurrency.code] += part.calculatedPrice.basePrice;
            if (includeTax) {
              if (!result.Currencies[targetCurrency]) {
                result.Currencies[targetCurrency] = 0;
              }

              result.Currencies[targetCurrency] += part.calculatedPrice.taxInTargetCurrency;
            }

            result.Conversion += part.calculatedPrice.conversionFee;
            result.Tax += part.calculatedPrice.taxInTargetCurrency;
            result.Total += part.calculatedPrice.fullPriceInTargetCurrency;
            result.Price += part.calculatedPrice.priceInTargetCurrency;
          }
        });
      });
    }

    if (!!route?.containerRentCalculatedPrice && !!route?.containerRent) {
      const sourceCurrency = currencies.find((c) => c.id === route?.containerRent.currency_id);

      if (!!sourceCurrency) {
        if (!result.Currencies[sourceCurrency.code]) {
          result.Currencies[sourceCurrency.code] = 0;
        }

        result.Currencies[sourceCurrency.code] += route.containerRentCalculatedPrice.basePrice;
        if (includeTax) {
          if (!result.Currencies[targetCurrency]) {
            result.Currencies[targetCurrency] = 0;
          }

          result.Currencies[targetCurrency] +=
            route.containerRentCalculatedPrice.taxInTargetCurrency;
        }

        result.Conversion += route.containerRentCalculatedPrice.conversionFee;
        result.Tax += route.containerRentCalculatedPrice.taxInTargetCurrency;
        result.Total += route.containerRentCalculatedPrice.fullPriceInTargetCurrency;
        result.Price += route.containerRentCalculatedPrice.priceInTargetCurrency;
      }
    }

    if (!!route?.dropOffCalculatedPrice && !!route?.dropOff) {
      const sourceCurrency = currencies.find((c) => c.id === route?.dropOff.currency_id);

      if (!!sourceCurrency) {
        if (!result.Currencies[sourceCurrency.code]) {
          result.Currencies[sourceCurrency.code] = 0;
        }

        result.Currencies[sourceCurrency.code] += route.dropOffCalculatedPrice.basePrice;
        if (includeTax) {
          if (!result.Currencies[targetCurrency]) {
            result.Currencies[targetCurrency] = 0;
          }

          result.Currencies[targetCurrency] += route.dropOffCalculatedPrice.taxInTargetCurrency;
        }

        result.Conversion += route.dropOffCalculatedPrice.conversionFee;
        result.Tax += route.dropOffCalculatedPrice.taxInTargetCurrency;
        result.Total += route.dropOffCalculatedPrice.fullPriceInTargetCurrency;
        result.Price += route.dropOffCalculatedPrice.priceInTargetCurrency;
      }
    }

    if (!!route?.pickOnCalculatedPrice && !!route?.pickOn) {
      const sourceCurrency = currencies.find((c) => c.id === route?.pickOn.currency_id);

      if (!!sourceCurrency) {
        if (!result.Currencies[sourceCurrency.code]) {
          result.Currencies[sourceCurrency.code] = 0;
        }

        result.Currencies[sourceCurrency.code] += route.pickOnCalculatedPrice.basePrice;
        if (includeTax) {
          if (!result.Currencies[targetCurrency]) {
            result.Currencies[targetCurrency] = 0;
          }

          result.Currencies[targetCurrency] += route.pickOnCalculatedPrice.taxInTargetCurrency;
        }

        result.Conversion += route.pickOnCalculatedPrice.conversionFee;
        result.Tax += route.pickOnCalculatedPrice.taxInTargetCurrency;
        result.Total += route.pickOnCalculatedPrice.fullPriceInTargetCurrency;
        result.Price += route.pickOnCalculatedPrice.priceInTargetCurrency;
      }
    }
  });

  return result;
};

export default getRouteSummary;
